import { useQuery } from '@apollo/client'
import { Scan, Lock, Package } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/sv/sidebar'
import { useCompanyPath, useIdCompany, useRBAC } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { WEBHOOK_QUERY } from '@/routes/webhook/queries'
import type {
  WebhookQuery,
  WebhookQueryVariables,
} from '@/routes/webhook/queries.types.gen'

const items = [
  {
    title: 'Overview',
    url: '/webhook/:idWebhook/overview',
    icon: Scan,
    activeTags: ['overview'],
  },
  {
    title: 'Event deliveries',
    url: '/webhook/:idWebhook/event-deliveries',
    icon: Package,
    activeTags: ['event-deliveries'],
  },
] as const

export const WebhookNav = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const location = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const { idWebhook = '' } = useParams<{ idWebhook: string }>()
  const { data } = useQuery<WebhookQuery, WebhookQueryVariables>(
    WEBHOOK_QUERY,
    {
      variables: { idCompany, idWebhook },
    }
  )
  const hasAccess = useMemo(
    () =>
      data === undefined
        ? true
        : rbac.company(idCompany).apps.create.can() &&
          rbac.company(idCompany).databases.create.can() &&
          rbac.company(idCompany).staticSites.create.can() &&
          rbac.company(idCompany).objectStorages.create.can(),
    [rbac, idCompany, data]
  )

  return (
    <SidebarGroup>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = item.activeTags.some((tag) =>
              location.pathname.includes(tag)
            )
            return (
              <SidebarMenuItem
                key={item.url}
                title={item.title}
                className={cn(
                  !hasAccess
                    ? 'opacity-50 cursor-not-allowed pointer-events-none'
                    : undefined
                )}
              >
                <SidebarMenuButton
                  asChild
                  isActive={isActive}
                  disabled={!hasAccess}
                >
                  {hasAccess ? (
                    <Link
                      to={getCompanyPath({
                        pathname: item.url,
                        variables: { idWebhook },
                      })}
                    >
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Lock />
                      <span>{item.title}</span>
                    </div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
